// src/utils/tauriHelper.js
// Helper for optional Tauri imports (desktop app only)

const TAURI_RUNTIME_HINT_KEY = 'thryveloop_tauri_runtime_hint_v1';
let cachedInvoke = null;
let importProbeAttempted = false;
let importProbeFailed = false;
let runtimeBridgeProbeAttempted = false;
let runtimeBridgeProbeSucceeded = false;

const looksLikeTauriLocation = () => {
  if (typeof window === 'undefined') return false;

  try {
    const protocol = (window.location?.protocol || '').toLowerCase();
    const hostname = (window.location?.hostname || '').toLowerCase();

    if (protocol === 'tauri:') return true;
    if (hostname === 'tauri.localhost' || hostname.endsWith('.tauri.localhost')) return true;
    return false;
  } catch (_) {
    return false;
  }
};

const hasGlobalTauriBridge = () => {
  if (typeof window === 'undefined') return false;
  return Boolean(
    window.__TAURI__ ||
    window.__TAURI_INTERNALS__ ||
    window.__TAURI_METADATA__ ||
    window.__TAURI_IPC__ ||
    window.__TAURI_INVOKE__
  );
};

const hasUsableGlobalInvoke = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (typeof window.__TAURI_INTERNALS__?.invoke === 'function') return true;
    if (typeof window.__TAURI_INVOKE__ === 'function') return true;
    if (typeof window.__TAURI__?.invoke === 'function') return true;
    return false;
  } catch (_) {
    return false;
  }
};

const isMissingTauriBridgeError = (value) => {
  const text = (value || '').toString().toLowerCase();
  if (!text) return false;
  if (text.includes('__tauri_internals__') || text.includes('__tauri__')) return true;
  if ((text.includes('undefined is not an object') || text.includes('cannot read properties of undefined')) && text.includes('invoke')) {
    return true;
  }
  return false;
};

const probeInvokeBridge = async (invoke) => {
  if (typeof invoke !== 'function') return false;
  if (runtimeBridgeProbeAttempted) return runtimeBridgeProbeSucceeded;
  runtimeBridgeProbeAttempted = true;
  try {
    // Unknown command is expected; we only care that the bridge call path exists.
    await invoke('__thryveloop_runtime_probe__');
    runtimeBridgeProbeSucceeded = true;
  } catch (error) {
    const message = `${error?.message || ''}\n${error?.stack || ''}`;
    runtimeBridgeProbeSucceeded = !isMissingTauriBridgeError(message);
  }
  return runtimeBridgeProbeSucceeded;
};

const hasCachedRuntimeHint = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage?.getItem(TAURI_RUNTIME_HINT_KEY) === '1';
  } catch (_) {
    return false;
  }
};

const markRuntimeHint = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage?.setItem(TAURI_RUNTIME_HINT_KEY, '1');
  } catch (_) {
    // Ignore storage failures.
  }
};

const clearRuntimeHint = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage?.removeItem(TAURI_RUNTIME_HINT_KEY);
  } catch (_) {
    // Ignore storage failures.
  }
};

/**
 * Check if running in Tauri environment.
 *
 * Notes:
 * - Legacy builds may expose `window.__TAURI__` / `window.__TAURI_INTERNALS__`.
 * - Newer Tauri runtimes often serve assets from `https://tauri.localhost`.
 * - Some desktop sessions only become detectable after the first successful
 *   dynamic import of the Tauri API.
 */
export const isTauriApp = () => {
  if (typeof window === 'undefined') return false;
  if (cachedInvoke) return true;
  if (hasUsableGlobalInvoke()) return true;
  if (looksLikeTauriLocation()) return true;
  if (hasCachedRuntimeHint() && hasGlobalTauriBridge()) return true;
  return false;
};

/**
 * Safely import Tauri API.
 * Returns null if module not available.
 *
 * Pass `{ forceProbe: true }` in critical user flows where runtime detection
 * must be verified even if global Tauri markers are absent.
 */
export const getTauriInvoke = async (options = {}) => {
  const forceProbe = Boolean(options?.forceProbe);
  if (cachedInvoke) {
    return cachedInvoke;
  }

  const shouldAttemptImport = forceProbe || isTauriApp();
  if (!shouldAttemptImport) {
    return null;
  }

  if (importProbeAttempted && importProbeFailed) {
    return null;
  }

  importProbeAttempted = true;

  try {
    // Dynamic import - works in desktop runtime and fails gracefully on web.
    const core = await import('@tauri-apps/api/core');
    const hasInvokeExport = typeof core?.invoke === 'function';
    let runtimeConfirmed = hasUsableGlobalInvoke();
    if (!runtimeConfirmed && hasInvokeExport) {
      runtimeConfirmed = await probeInvokeBridge(core.invoke);
    }

    if (hasInvokeExport && runtimeConfirmed) {
      cachedInvoke = core.invoke;
      importProbeFailed = false;
      markRuntimeHint();
      return cachedInvoke;
    }
    // `@tauri-apps/api` may be importable in web bundles; do not treat that
    // as a valid desktop runtime unless the bridge is actually available.
    clearRuntimeHint();
    importProbeFailed = true;
    return null;
  } catch (error) {
    importProbeFailed = true;
    clearRuntimeHint();
    if (isTauriApp()) {
      console.warn('Tauri API not available:', error);
    }
    return null;
  }
};

export default {
  isTauriApp,
  getTauriInvoke,
};
